import { Dialog, Slide } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import SignIn from "./signIn";
import ForgotPassword from "./forgotPassword";
import { useNavigate } from "react-router-dom";
import SetupAccount from "./setupAccount";
import AccountCreated from "./accountCreated";
import CreateAccount from "./createAccount";
import { useAuth } from "../../hooks/useAuth";
import { getAuth } from 'firebase/auth'

export type LoginFormType = 'signin' | 'signup' | 'forgotpassword' | 'setupaccount' | 'accountcreated';

interface TransitionProps {
  children: React.ReactElement<any, any>;
}
const Transition = React.forwardRef(function Transition(props: TransitionProps, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export interface LoginModalVisibleType {
  isOpen: boolean;
  formType: LoginFormType;
  currentStep?: number;
  redirectOptions?: {
    redirectURL?: string;
    redirectState?: {[key: string]: any};
  },
}

export interface LoginFormProps {
  setFormType: React.Dispatch<React.SetStateAction<LoginFormType>>;
  handleClose: (event?: any, reason?: any, shouldRedirect?: boolean) => void;
  setCurrentStep?: React.Dispatch<React.SetStateAction<number>>;
  currentStep?: number;
}

const LoginModal: React.FC = () => {
  const { loginModalVisible, setLoginModalVisible, authUser } = useAuth();
  const [ formType, setFormType ] = useState<LoginFormType>(loginModalVisible.formType);
  const [ redirectURL, setRedirectURL ] = useState<string>(null);
  const [ redirectState, setRedirectState ] = useState<Record<string, any>>(null);
  const [ currentStep, setCurrentStep ] = useState<number>(loginModalVisible.currentStep || 1);
  const navigate = useNavigate();
  const auth = getAuth()
  const isAnon = auth.currentUser?.isAnonymous === true


  useEffect(() => {
    if (formType !== loginModalVisible.formType) {
      setFormType(loginModalVisible.formType)
    }

    if (loginModalVisible.isOpen) {
      // store redirect information
      if (loginModalVisible.redirectOptions) {
        if (loginModalVisible.redirectOptions.redirectURL) {
          setRedirectURL(loginModalVisible.redirectOptions.redirectURL)
        }
        if (loginModalVisible.redirectOptions.redirectState) {
          setRedirectState(loginModalVisible.redirectOptions.redirectState)
        }
      }
    } else {
      // reset if closed
      setRedirectURL(null)
      setRedirectState(null)
    }
  }, [loginModalVisible]) //eslint-disable-line react-hooks/exhaustive-deps

  // Add effect to handle transition to setupaccount after first login
  useEffect(() => {
    if (!authUser?.documentId) return;

    const emailVerified = auth.currentUser?.emailVerified;
    const { creationTime, lastSignInTime } = auth.currentUser.metadata;
    const isFirstLogin = creationTime === lastSignInTime;

    if (emailVerified === false) {
      setCurrentStep(3);
    } else if (isFirstLogin) {
      setFormType('setupaccount');
    }
  }, [authUser]);

  useEffect(() => {
    if (loginModalVisible.isOpen) {
      // whenever the modal is (re-)opened, force our local state to match the incoming one
      setFormType(loginModalVisible.formType);
      setCurrentStep(loginModalVisible.currentStep ?? 1);
    }
    }, [
      loginModalVisible.isOpen,
      loginModalVisible.formType,
      loginModalVisible.currentStep,
    ]);

  const handleClose = useCallback((event: any, reason: any, shouldRedirect?: boolean) => {
    if (reason === 'creditsExhausted') {
      setFormType('signup');
      setCurrentStep(1);
      return;
    }

    // only early-return for signup step 3
    if (
      formType !== 'setupaccount' &&
      !(formType === 'signup' && currentStep === 3)
    ) {
      setLoginModalVisible(info => ({ ...info, isOpen: false }));
      
      if (shouldRedirect) {
        // redirect user as required
        if (redirectURL) {
          navigate(redirectURL, {state: redirectState});
        }
      }
    }
  }, [formType, currentStep, loginModalVisible]);

  const renderSwitch = () => {
    switch (formType) {
      case 'forgotpassword':
        return <ForgotPassword setFormType={setFormType} handleClose={handleClose} />
      case 'signup':
        return <CreateAccount
          setFormType={setFormType}
          handleClose={handleClose}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        />
      case 'setupaccount':
        return <SetupAccount setFormType={setFormType} handleClose={handleClose} />
      case 'accountcreated':
        return <AccountCreated setFormType={setFormType} handleClose={handleClose} />
      default :
        return <SignIn
          setFormType={setFormType}
          handleClose={handleClose}
          setCurrentStep={setCurrentStep}
        />
    }
  }

  return (
    <Dialog
      open={loginModalVisible.isOpen}
      TransitionComponent={Transition}
      onClose={(event, reason) => {
        // block backdrop clicks on signup steps 1–2
        if (formType === 'signup' && currentStep < 3 && reason === 'backdropClick') {
          return;
        }
        handleClose(event, reason);
      }}
      onBackdropClick={() => {}}
      disableEscapeKeyDown={formType === 'signup' && currentStep < 3}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: {
            xs: 0,
            sm: 4,
          },
          maxWidth: {
            xs: '100%',
            sm: '425px',
          },
          m: 0,
          width: '100%',
          height: {
            xs: '100%',
            sm: 'fit-content',
          },
          minHeight: {
            xs: '100%',
            sm: 'auto',
          }
        },
      }}
    >
      { renderSwitch() }
    </Dialog>
  )
}

export default LoginModal;
